import { apiClient } from '@/shared/api/client'
import type { ApiResponse } from '@/types'
import type { AxiosResponse, AxiosRequestConfig, Method } from 'axios'

interface RequestOptions {
  method?: string
  headers?: Record<string, string>
  body?: BodyInit | null
}

export function useApiRequest() {
  const makeRequest = async <T = unknown>(
    url: string,
    options: RequestOptions = {},
    queryParams?: Record<string, string | number | boolean | string[]>,
  ): Promise<T> => {
    const config: AxiosRequestConfig = {
      url,
      method: (options.method || 'GET') as Method,
      headers: { ...options.headers },
    }

    if (options.body instanceof FormData) {
      config.data = options.body
      delete config.headers!['Content-Type']
    } else if (options.body !== undefined && options.body !== null) {
      config.data = options.body
      config.headers!['Content-Type'] =
        (config.headers!['Content-Type'] as string | undefined) ?? 'application/json'
    }

    if (queryParams && Object.keys(queryParams).length > 0) {
      const params: Record<string, string | string[]> = {}
      for (const [key, value] of Object.entries(queryParams)) {
        if (Array.isArray(value)) {
          params[`${key}[]`] = value.map((v) => String(v))
        } else if (value !== null && value !== undefined && value !== '') {
          params[key] = String(value)
        }
      }
      config.params = params
    }

    let response: AxiosResponse<unknown>
    try {
      response = await apiClient.request(config)
    } catch (error) {
      const axiosErr = error as { response?: AxiosResponse; message?: string }
      const errorData = (axiosErr.response?.data ?? {}) as Record<string, unknown>
      const message =
        (typeof errorData['message'] === 'string' ? errorData['message'] : undefined) ||
        (typeof errorData['error'] === 'string' ? errorData['error'] : undefined) ||
        axiosErr.message ||
        `HTTP error! status: ${axiosErr.response?.status ?? 0}`
      // Keep parity with success:false JSON responses below - callers use try/catch.
      throw new Error(message)
    }

    const data = response.data
    const apiResponse = data as ApiResponse<T>
    if (apiResponse.success !== undefined) {
      if (apiResponse.success) {
        const responseData = apiResponse.data as Record<string, unknown> | undefined
        if (responseData?.['token'] || (apiResponse.meta && apiResponse.meta['token'])) {
          return data as T
        }
        return apiResponse.data as T
      }

      throw new Error(apiResponse.message || 'API request failed')
    }

    throw new Error('Unexpected API response format')
  }

  return {
    makeRequest,
  }
}
